<template>
  <div :id="`font-picker${pickerSuffix}`" class="font-picker" :title="state.errorText">
    <button
      class="dropdown-button"
      type="button"
      :class="{ expanded: state.expanded }"
      @click="toggleExpanded"
      @keypress="toggleExpanded"
    >
      <p class="dropdown-font-name">{{ state.activeFont.family }}</p>
      <p class="dropdown-icon" :class="state.loadingStatus"></p>
    </button>

    <ul v-if="state.loadingStatus === 'finished' && visibleFonts.length"
        :class="{ expanded: state.expanded }"
        @scroll="onScroll"
    >
      <li v-for="font in visibleFonts" :key="font.family">
        <button
          type="button"
          :class="`font-${snakeCase(font.family)}${pickerSuffix} ${font.family === state.activeFont.family ? 'active-font' : ''}`"
          @click="itemClick(font)"
          @keypress="itemClick(font)"
        >
          {{ font.family }}
        </button>
      </li>
      <li v-if="state.loadingMore" class="loading-more">
        <div class="spinner"></div>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  props: ['activeFont', 'options'],
  data() {
    return {
      apiBaseUrl: 'sitebypro-server.ru',
      apiUrl: 'https://sitebypro-server.ru/fonts',
      state: {
        activeFont: { family: this.activeFont || '' },
        errorText: '',
        expanded: false,
        loadingStatus: 'loading',
        loadingMore: false
      },
      pickerSuffix: '',
      visibleFonts: [],
      allFonts: [],
      currentIndex: 0,
      batchSize: 50,
      hasMoreFonts: true
    };
  },
  mounted() {
    this.pickerSuffix = this.options?.name ? `-${this.options.name}` : '';
    this.initializeFontManager();
  },
  watch: {
    activeFont(newVal) {
      if (this.state.activeFont.family !== newVal) {
        this.setActiveFontByFamily(newVal);
      }
    }
  },
  methods: {
    async initializeFontManager() {
      try {
        const response = await fetch(this.apiUrl);
        if (!response.ok) throw new Error('Network response was not ok');

        const data = await response.json();
        this.allFonts = data.map(font => this.transformFontData(font));

        this.loadMoreFonts();
        this.setState({ errorText: '', loadingStatus: 'finished' });
      } catch (err) {
        this.setState({ errorText: 'Ошибка загрузки шрифтов', loadingStatus: 'error' });
        console.error(err);
      }
    },

    transformFontData(font) {
      const fullUrl = this.apiBaseUrl + font.url;
      return {
        family: font.name,
        variants: ['regular'],
        subsets: ['cyrillic', 'latin'],
        version: 'v1',
        lastModified: new Date().toISOString().split('T')[0],
        files: { regular: fullUrl },
        category: 'sans-serif',
        kind: 'webfonts#webfont',
        menu: fullUrl,
        format: font.format,
        url: fullUrl
      };
    },

    loadMoreFonts() {
      if (this.state.loadingMore || !this.hasMoreFonts) return;

      this.setState({ loadingMore: true });

      const nextBatch = this.allFonts.slice(this.currentIndex, this.currentIndex + this.batchSize);
      if (!nextBatch.length) {
        this.hasMoreFonts = false;
        this.setState({ loadingMore: false });
        return;
      }

      this.visibleFonts = [...this.visibleFonts, ...nextBatch];
      this.currentIndex += this.batchSize;

      this.loadFontPreviews(nextBatch);
      this.setState({ loadingMore: false });
    },

    loadFontPreviews(fonts) {
      fonts.forEach(font => {
        const styleElement = document.createElement('style');
        const fontClass = `.font-${this.snakeCase(font.family)}${this.pickerSuffix}`;
        styleElement.textContent = `
          @font-face {
            font-family: "${font.family}";
            src: url("${font.url}") format("${font.format}");
            font-weight: normal;
            font-style: normal;
          }
          ${fontClass} { font-family: "${font.family}", sans-serif; }
        `;
        document.head.appendChild(styleElement);
        setTimeout(() => {
          if (document.head.contains(styleElement)) document.head.removeChild(styleElement);
        }, 3000);
      });
    },

    async itemClick(font) {
      this.toggleExpanded();
      this.setState({ loadingStatus: 'loading' });

      try {
        await this.ensureFontLoaded(font.family, font.url);
        this.setActiveFont(font);
      } catch (err) {
        console.error('Font load error', err);
        this.setActiveFont(font);
      } finally {
        this.setState({ loadingStatus: 'finished' });
      }
    },

    async ensureFontLoaded(family, url) {
      if (!document.fonts) return;
      const fontFace = new FontFace(family, `url(${url})`);
      try { await fontFace.load(); document.fonts.add(fontFace); } catch(e){}
      const start = Date.now();
      while (Date.now() - start < 200) {
        if (document.fonts.check(`16px "${family}"`)) return;
        await this.wait(30);
      }
    },

    wait(ms) { return new Promise(res => setTimeout(res, ms)); },

    setActiveFont(font) {
      this.setState({ activeFont: font, errorText: '' });
      this.$emit('change', font);
    },

    setActiveFontByFamily(family) {
      const font = this.allFonts.find(f => f.family === family);
      if (font) this.setActiveFont(font);
    },

    toggleExpanded() { this.setState({ expanded: !this.state.expanded }); },

    onScroll(e) {
      const element = e.target;
      const scrollBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
      if (scrollBottom < 1000 && !this.state.loadingMore && this.hasMoreFonts) this.loadMoreFonts();
    },

    snakeCase(text) { return text.replace(/\s+/g, '-').toLowerCase(); },

    setState(state) { this.state = Object.assign(this.state, state); }
  }
};
</script>

<style>
.font-picker { position: relative; display: inline-block; width: 100%rem; box-shadow: 0.0625rem 0.0625rem 0.1875rem rgba(0, 0, 0, 0.2); }
.font-picker * { box-sizing: border-box; }
.font-picker p { margin: 0; padding: 0; }
.font-picker button { background: none; border: 0; color: inherit; cursor: pointer; font-size: inherit; outline: none; }
.font-picker .dropdown-button { height: 2.1875rem; width: 100%; display: flex; align-items: center; justify-content: space-between; padding: 0 0.625rem; background: red; }
.font-picker .dropdown-button:hover, .font-picker .dropdown-button.expanded, .font-picker .dropdown-button:focus {}
.font-picker .dropdown-button .dropdown-font-name { overflow: hidden; white-space: nowrap; }
.font-picker .dropdown-button.expanded .dropdown-icon.finished:before { -webkit-transform: rotate(-180deg); transform: rotate(-180deg); }
.font-picker .dropdown-icon { margin-left: 0.625rem; }
.font-picker .dropdown-icon.loading:before { content: ''; display: block; height: 0.625rem; width: 0.625rem; border-radius: 0.5rem; border: 0.125rem solid #b2b2b2; border-top-color: black; -webkit-animation: spinner 0.6s linear infinite; animation: spinner 0.6s linear infinite; }
.font-picker .dropdown-icon.finished:before { content: ''; display: block; height: 0; width: 0; border-left: 0.5rem solid transparent; border-right: 0.5rem solid transparent; border-top: 0.375rem solid black; transition: transform 0.3s; margin: 0 0.125rem; }
.font-picker .dropdown-icon.error:before { content: '⚠'; }
.font-picker ul { position: absolute; z-index: 1; max-height: 0; width: 100%; overflow-x: hidden; overflow-y: auto; -webkit-overflow-scrolling: touch; margin: 0; padding: 0; background: #EAEAEA; box-shadow: 0.0625rem 0.0625rem 0.1875rem rgba(0, 0, 0, 0.2); transition: 0.3s; }
.font-picker ul.expanded { max-height: 12.5rem; background-color: var(--grey-1);}
.font-picker ul li { height: 2.1875rem; list-style: none; }
.font-picker ul li button { height: 100%; width: 100%; display: flex; align-items: center; padding: 0 0.625rem; white-space: nowrap; }
.font-picker ul li button:hover, .font-picker ul li button:focus { background: #dddddd; }
.font-picker ul li button.active-font { background: #d1d1d1; }
@-webkit-keyframes spinner { to { -webkit-transform: rotate(360deg); transform: rotate(360deg); } }
@keyframes spinner { to { -webkit-transform: rotate(360deg); transform: rotate(360deg); } }
.dropdown-button { background: var(--grey-0) !important; color: var(--color-text) !important; }
.dropdown-icon { filter: invert(1); }
</style>
